import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { getSvgPointFromMouseClick } from "../../../utils/map/svg-helper";
import IMapCrop from "../../../interfaces/robot/zone-planner/map-crop";
import { selectCroppedComponents, selectMapCrop, selectMapPutStatus } from "../../../store/room/maps/maps-selectors";
import ConfirmAbortDialog from "../../General/ConfirmAbortDialog";
import CustomAlert from "../../Feedback/CustomAlert";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";
import getSaveErrorMessages from "../../../functions/general/get-save-error-messages";
import CustomSnackbar from "../../Feedback/CustomSnackbar";

interface ICropRectangle {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

const ZonePlannerMapCrop: React.FC<{
  roomId: string;
  mapWidth: number;
  mapHeight: number;
  zoomScale: number;
}> = ({ mapWidth, mapHeight, zoomScale, roomId }) => {
  const { t } = useTranslation();

  const isMouseDown = useRef(false);
  const svgRef = useRef<SVGSVGElement>(null);

  const [cropRect, setCropRect] = useState<ICropRectangle | null>(null);

  const dispatch = useSharedDispatch();

  const { resetCroppedComponents, setMapCrop, putMapByRoomId, resetMapPutStatus } = useSharedActions();

  const croppedComponents = useSharedSelector((state) => selectCroppedComponents(state));
  const mapCrop = useSharedSelector((state) => selectMapCrop(state));
  const mapPutStatus = useSharedSelector((state) => selectMapPutStatus(state));

  const mouseTouchDownHandler = (event: React.MouseEvent | React.TouchEvent) => {
    isMouseDown.current = true;
    dispatch(resetCroppedComponents());
    const svgPoint = getSvgPointFromMouseClick(event, svgRef, zoomScale);
    if (svgPoint != null) {
      setCropRect({ x1: svgPoint.x, y1: svgPoint.y, x2: svgPoint.x, y2: svgPoint.y });
    }
  };

  const mouseUpTouchEndHandler = (_event: React.MouseEvent | React.TouchEvent) => {
    isMouseDown.current = false;
    if (cropRect != null) {
      dispatch(
        setMapCrop({
          x: Math.floor(Math.min(cropRect.x1, cropRect.x2)),
          y: Math.floor(Math.min(cropRect.y1, cropRect.y2)),
          width: Math.floor(Math.abs(cropRect.x2 - cropRect.x1)),
          height: Math.floor(Math.abs(cropRect.y2 - cropRect.y1)),
        } as IMapCrop),
      );
    }
  };

  const moveHandler = (event: React.MouseEvent | React.TouchEvent) => {
    if (cropRect != null && isMouseDown.current) {
      const svgPoint = getSvgPointFromMouseClick(event, svgRef, zoomScale);
      if (svgPoint != null) {
        setCropRect((prev) => (prev != null ? { x1: prev.x1, y1: prev.y1, x2: svgPoint.x, y2: svgPoint.y } : null));
      }
    }
  };

  const abortCropHandler = () => {
    dispatch(resetCroppedComponents());
  };

  const confirmCropClickHandler = () => {
    if (mapCrop != null) {
      dispatch(putMapByRoomId({ roomId, mapCrop }));
    }
  };

  const handleMapPutErrorSnackbarClose = () => {
    dispatch(resetMapPutStatus());
  };

  return (
    <React.Fragment>
      <svg
        viewBox={`0 0 ${mapWidth} ${mapHeight}`}
        ref={svgRef}
        style={{
          backgroundColor: cropRect == null ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,0)",
          position: "absolute",
          top: 0,
          left: 0,
          width: mapWidth,
          height: mapHeight,
          cursor: "crosshair",
        }}
        onMouseDown={mouseTouchDownHandler}
        onTouchStart={mouseTouchDownHandler}
        onMouseUp={mouseUpTouchEndHandler}
        onTouchEnd={mouseUpTouchEndHandler}
        onMouseMove={moveHandler}
        onTouchMove={moveHandler}
        onContextMenu={(e) => e.preventDefault()}
      >
        {cropRect != null && (
          <React.Fragment>
            <mask id="mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={Math.min(cropRect.x1, cropRect.x2)}
                y={Math.min(cropRect.y1, cropRect.y2)}
                width={Math.abs(cropRect.x2 - cropRect.x1)}
                height={Math.abs(cropRect.y2 - cropRect.y1)}
                fill="black"
              />
            </mask>
            <rect width="100%" height="100%" fill="black" fillOpacity="0.5" mask="url(#mask)" />

            <rect
              x={Math.min(cropRect.x1, cropRect.x2)}
              y={Math.min(cropRect.y1, cropRect.y2)}
              width={Math.abs(cropRect.x2 - cropRect.x1)}
              height={Math.abs(cropRect.y2 - cropRect.y1)}
              style={{ stroke: "black", strokeWidth: 2, strokeDasharray: "10 10", fill: "rgba(0,0,0,0)" }}
            />
          </React.Fragment>
        )}
      </svg>
      <ConfirmAbortDialog
        isOpen={mapCrop != null}
        title={t("save-cropped-map-dialog-title")}
        description={
          croppedComponents == null ? undefined : (
            <React.Fragment>
              {mapPutStatus?.status === HttpFetchStatus.Failed && (
                <CustomAlert
                  header={t("error-saving-room")}
                  onClose={handleMapPutErrorSnackbarClose}
                  textMessages={getSaveErrorMessages(mapPutStatus.error)}
                  severity="error"
                />
              )}
              <Typography>{t("save-cropped-map-dialog-description")}</Typography>
              {croppedComponents != null &&
                (croppedComponents.gates.length > 0 ||
                  croppedComponents.zones.length > 0 ||
                  croppedComponents.waypoints > 0) && (
                  <CustomAlert
                    severity="warning"
                    header={t("warning-components-will-be-deleted-for-crop")}
                    textMessages={[
                      ...croppedComponents.zones,
                      ...croppedComponents.gates,
                      croppedComponents.waypoints > 0 ? `${croppedComponents.waypoints} ${t("waypoints-plural")}` : "",
                    ]}
                  />
                )}
            </React.Fragment>
          )
        }
        abortButtonText={t("button-cancel")}
        confirmButtonText={t("button-confirm")}
        onConfirm={confirmCropClickHandler}
        onAbort={abortCropHandler}
      />
    </React.Fragment>
  );
};

export default ZonePlannerMapCrop;
